import { listCategoriesQuery } from "@/api/category";
import { searchResourcesQuery } from "@/api/resources";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { titleCase } from "@common/utils/string";
import { Flex, Text } from "@givenwell/components";
import { ResourceBrief } from "@givenwell/management-api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { ReactNode } from "react";
import { fallback, number, object, optional, pipe, string, toMinValue } from "valibot";

const route = getRouteApi("/admin/resources/");

export const Route = createFileRoute("/admin/resources/")({
  component: ResourcesPage,
  validateSearch: object({
    q: fallback(optional(string(), ""), ""),
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 100), toMinValue(10)), 100),
  }),
});

function ResourcesPage() {
  const { q, "page-size": pageSize, page } = route.useSearch();

  const navigate = useNavigate();

  const query = useQuery(searchResourcesQuery(q, page, pageSize));
  const categoriesQuery = useQuery(listCategoriesQuery("resource"));

  function handleNewResource() {
    navigate({
      to: "/admin/resources/$resourceId",
      params: {
        resourceId: "new",
      },
    });
  }

  return (
    <PageWrapper
      title="Resources"
      rightContent={
        <Button variant="tonal" color="primary" onClick={handleNewResource}>
          New resource
        </Button>
      }
    >
      <DataTable>
        <DataTableHeader autoFocusSearch></DataTableHeader>
        <DataTableBody
          data={query.data?.items ?? []}
          status={query.status}
          columns={[
            {
              header: "Title",
              cell: row => (
                <StyledLink to="/admin/resources/$resourceId" params={{ resourceId: row.id }}>
                  {row.title}
                </StyledLink>
              ),
            },
            {
              header: "Resource Type",
              cell: resourceTypeColumnSelector,
            },
            {
              header: "Provider",
              cell: resource => titleCase(resource.provider),
            },
            {
              header: "Category",
              cell: row => categoriesQuery.data?.categories.find(c => c.category_id === row.category_id)?.name,
            },
          ]}
          dataKey="id"
        />
        <DataTableFooter itemCount={query.data?.total_items} />
      </DataTable>
    </PageWrapper>
  );
}

function resourceTypeColumnSelector(row: ResourceBrief): ReactNode {
  switch (row.type) {
    case "audio":
      return (
        <Flex css={{ items: "center", gap: 8 }}>
          {/* <IconMusic size={14} /> */}
          <Text as="span">Audio</Text>
        </Flex>
      );

    case "video":
      return (
        <Flex css={{ items: "center", gap: 8 }}>
          {/* <IconMusic size={14} /> */}
          <Text as="span">Video</Text>
        </Flex>
      );

    case "ebook":
      return (
        <Flex css={{ items: "center", gap: 8 }}>
          {/* <IconMusic size={14} /> */}
          <Text as="span">E-Book</Text>
        </Flex>
      );

    case "pdf":
      return (
        <Flex css={{ items: "center", gap: 8 }}>
          {/* <IconMusic size={14} /> */}
          <Text as="span">PDF</Text>
        </Flex>
      );

    case "article":
      return (
        <Flex css={{ items: "center", gap: 8 }}>
          {/* <IconMusic size={14} /> */}
          <Text as="span">Article</Text>
        </Flex>
      );

    default:
      return <>Unknown</>;
  }
}
